import React from 'react'
import injectJss from 'react-jss'
import compose from 'recompose/compose'
import classNames from 'classnames'
import text from 'public/assets/svg/hotspots/fill_circle.svg'
import photo from 'public/assets/svg/hotspots/photo_hotspot.svg'
import video from 'public/assets/svg/hotspots/video_hotspot.svg'
import panorama from 'public/assets/svg/hotspots/360_hotspot.svg'
import positionalMedia from 'public/assets/svg/positional_media/play_i.svg'
import CHECK_CIRCLE from 'public/assets/svg/check_circle.svg'

const getSizeFromState = ({ currentState, type }) => {
    if (currentState === 'list') {
        return {
            height: '100%',
            width: '100%',
        }
    }
    switch (type) {
        case 'text': {
            if (currentState === 'default') {
                return {
                    height: 30,
                    width: 30,
                }
            } else {
                return {
                    height: 68,
                    width: 68,
                }
            }
        }
        default: {
            return {
                height: 50,
                width: 50,
            }
        }
    }
}

const style = theme => ({
    wrapper: {
        width: ({ currentState, type }) => getSizeFromState({ currentState, type }).width,
        height: ({ currentState, type }) => getSizeFromState({ currentState, type }).height,
        position: 'relative',
        cursor: 'pointer',
        pointerEvents: 'all',
        zIndex: ({ currentState }) => (currentState === 'list' ? 0 : 200),
        transition: '0.2s ease-out',
        '& *': {
            transition: '0.2s ease-out',
        },
    },
    border: {
        border: '1.3px solid #fff',
        width: 'calc(100% + 2px)',
        height: 'calc(100% + 2px)',
        borderRadius: '50%',
        position: 'absolute',
        boxSizing: 'border-box',
        top: -1,
        left: -1,
        animationDuration: ({ seed }) => `${4 + seed * 0.2}s`,
        animationDelay: ({ seed }) => `${0.2 + seed / 10}s`,
        animationName: ({ currentState }) => (currentState === 'default' ? 'borderSize' : 'undefined'),
        animationIterationCount: 'infinite',
        pointerEvents: 'none',
    },
    background: {
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        backgroundColor: '#000',
        opacity: 0.25,
        transition: 'opacity 0.2s ease-out',
        borderRadius: '50%',
        animationDuration: ({ seed }) => `${4 + seed * 0.2}s`,
        animationDelay: ({ seed }) => `${0.2 + seed / 10}s`,
        animationName: ({ currentState }) => (currentState === 'default' ? 'iconBackgroundDisplay' : 'undefined'),
        animationIterationCount: 'infinite',
    },
    iconContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
        opacity: 1,
    },
    icons: {
        position: 'relative',
        transform: 'scale(1)',
    },
    icon: {
        color: '#fff',
        position: 'absolute',
        transform: ({ currentState }) =>
            currentState === 'default'
                ? 'translateY(-50%) translateX(-50%) scale(0.8)'
                : 'translateY(-50%) translateX(-50%) scale(1.05)',
        top: 0,
        left: 0,
        transition: 'opacity 0.2s ease-out',
        opacity: 1,
        animationDuration: ({ seed }) => `${4 + seed * 0.2}s`,
        animationDelay: ({ seed }) => `${0.2 + seed / 10}s`,
        animationName: ({ currentState }) => (currentState === 'default' ? 'iconDisplay' : 'undefined'),
        animationIterationCount: 'infinite',
    },
    checkedIcon: {
        display: ({ currentState }) => (currentState === 'visited' ? 'block' : 'none'),
        position: 'absolute',
        top: '-1px',
        right: '-4px',
    },
    '@keyframes borderSize': {
        '0%': {
            transform: 'scale(1)',
            opacity: '0.5',
        },
        '15%': {
            transform: 'scale(1)',
            opacity: '0',
        },
        '25%': {
            transform: 'scale(1)',
            opacity: '0',
        },
        '30%': {
            transform: 'scale(1)',
            opacity: '1',
        },
        '45%': {
            transform: 'scale(3.6)',
            opacity: '0',
        },
        // for the delay between animations
        '100%': {
            transform: 'scale(6.4)',
            opacity: '0',
        },
    },
    '@keyframes iconDisplay': {
        '0%': {
            opacity: '0',
        },
        '10%': {
            opacity: '1',
        },
        '40%': {
            opacity: '1',
        },
        '50%': {
            opacity: '0',
        },
        // for the delay between animations
        '100%': {
            opacity: '0',
        },
    },
    '@keyframes iconBackgroundDisplay': {
        '0%': {
            opacity: '0',
        },
        '10%': {
            opacity: '0.35',
        },
        '40%': {
            opacity: '0.35',
        },
        '50%': {
            opacity: '0.1',
        },
        // for the delay between animations
        '100%': {
            opacity: '0',
        },
    },
})

const iconNames = {
    text,
    photo,
    video,
    panorama,
    positionalMedia,
}

const Hotspot = ({ classes, type, onMouseEnter, onClick, currentState }) => {
    return (
        <div
            className={classNames('hotspot', classes.wrapper)}
            onMouseEnter={onMouseEnter}
            onClick={onClick}
            onTouchStart={onClick}
            data-test={(currentState === 'visited' && 'HotspotVisited') || 'HotspotNotVisited'}
        >
            <div className={classNames(classes.background)} />
            <div className={classes.border} />
            <div className={classes.iconContainer}>
                <div className={classes.icons}>
                    <img
                        alt={`${type === 'positionalMedia' ? 'Play' : type}`}
                        className={classes.icon}
                        src={`${iconNames[type]}`}
                    />
                </div>
            </div>
            <img className={classes.checkedIcon} src={CHECK_CIRCLE} />
        </div>
    )
}

export default compose(injectJss(style))(Hotspot)
